// src/components/ui/BulkTagGuide.js
//
// 3-page guide for bulk toolbar actions. Positions itself above or below the
// BulkActionToolbar (16 px gap, matching PopoverWrapper sideOffset). An arrow
// slides horizontally between toolbar buttons as the page changes.

import React, { useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import GuidePanel from './GuidePanel';

const STEPS = [
  {
    title: 'Add Tags',
    body: 'Choose or create tags to add to all selected documents.',
    target: 'bulk-add-tags',
  },
  {
    title: 'Remove Tags',
    body: 'Choose tags to remove from all selected documents.',
    target: 'bulk-remove-tags',
  },
  {
    title: 'Delete Documents',
    body: 'Delete all selected documents.',
    target: 'bulk-delete',
  },
];

const GUIDE_WIDTH = 248;
const TOOLBAR_GAP = 16; // matches BulkDocumentSetSelector sideOffset

const ARROW_SIZE = 12;
// ARROW_HALF: used as negative marginLeft to center the rotated square on arrowLeft.
// ARROW_EDGE_INSET: how far the arrow center sits inside the panel edge so the tip
// protrudes cleanly without clipping the rounded corner.
const ARROW_HALF = ARROW_SIZE / 2;          // 6
const ARROW_EDGE_INSET = ARROW_HALF - 2;    // 4 → tip protrudes ~6.5 px past panel edge

/**
 * @param {boolean}  visible
 * @param {1|2|3}    step
 * @param {function} onNext
 * @param {function} onGotIt
 * @param {function} onClose
 * @param {function} onStepChange
 */
const BulkTagGuide = ({ visible, step, onNext, onGotIt, onClose, onStepChange }) => {
  const guideRef = useRef(null);
  const [pos, setPos] = useState(null);       // { left, top, arrowAtBottom }
  const [arrowLeft, setArrowLeft] = useState(GUIDE_WIDTH / 2);
  const [isClosing, setIsClosing] = useState(false);
  const [closingOrigin, setClosingOrigin] = useState('center center');
  const [mounted, setMounted] = useState(false);

  const recalc = useCallback(() => {
    const toolbar = document.querySelector('[data-guide-toolbar]');
    if (!toolbar) return;

    const toolbarRect = toolbar.getBoundingClientRect();
    const guideH = guideRef.current?.offsetHeight ?? 160;
    const spaceBelow = window.innerHeight - toolbarRect.bottom - TOOLBAR_GAP;
    const spaceAbove = toolbarRect.top - TOOLBAR_GAP;
    const showBelow = spaceBelow >= guideH || spaceBelow >= spaceAbove;

    let left = toolbarRect.left + toolbarRect.width / 2 - GUIDE_WIDTH / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - GUIDE_WIDTH - 8));

    const top = showBelow
      ? toolbarRect.bottom + TOOLBAR_GAP
      : toolbarRect.top - guideH - TOOLBAR_GAP;

    setPos({ left, top, arrowAtBottom: !showBelow });

    // Slide arrow to center of this step's target button
    const targetKey = STEPS[(step - 1) % STEPS.length]?.target;
    const targetEl = targetKey
      ? document.querySelector(`[data-guide-target="${targetKey}"]`)
      : null;
    let al = GUIDE_WIDTH / 2;
    if (targetEl) {
      const tr = targetEl.getBoundingClientRect();
      al = tr.left + tr.width / 2 - left;
      al = Math.max(14, Math.min(al, GUIDE_WIDTH - 14));
    }
    setArrowLeft(al);
  }, [step]);

  useLayoutEffect(() => {
    if (!visible) {
      setMounted(false);
      setArrowLeft(GUIDE_WIDTH / 2); // Reset so re-entry always starts from the correct step-1 position
      return;
    }
    recalc();
    // Re-run after the toolbar's 200 ms slideUpFadeIn animation so the initial
    // auto-trigger position matches the toolbar's final resting place.
    const t = setTimeout(recalc, 220);
    requestAnimationFrame(() => setMounted(true));
    return () => clearTimeout(t);
  }, [visible, step, recalc]);

  useEffect(() => {
    if (!visible) return;
    window.addEventListener('resize', recalc);

    // Reposition when the top-level layout container resizes (e.g. sidebar
    // open/close shifts the toolbar without triggering a window resize).
    const layoutContainer = document.querySelector('[data-guide-layout]');
    let ro;
    if (layoutContainer && window.ResizeObserver) {
      ro = new ResizeObserver(recalc);
      ro.observe(layoutContainer);
    }

    return () => {
      window.removeEventListener('resize', recalc);
      ro?.disconnect();
    };
  }, [visible, recalc]);

  const animateClose = useCallback((callback) => {
    const helpBtn = document.querySelector('[data-guide-help]');
    if (helpBtn && guideRef.current) {
      const hr = helpBtn.getBoundingClientRect();
      const gr = guideRef.current.getBoundingClientRect();
      setClosingOrigin(
        `${hr.left + hr.width / 2 - gr.left}px ${hr.top + hr.height / 2 - gr.top}px`
      );
    } else {
      setClosingOrigin('center center');
    }
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setMounted(false);
      callback?.();
    }, 220);
  }, []);

  const handleClose = useCallback(() => animateClose(onClose), [animateClose, onClose]);
  const handleGotIt = useCallback(() => animateClose(onGotIt), [animateClose, onGotIt]);

  if (!visible && !isClosing) return null;
  if (!pos) return null;

  return (
    <GuidePanel
      ref={guideRef}
      steps={STEPS}
      step={step}
      titlePrefix="Bulk Actions"
      onNext={onNext}
      onGotIt={handleGotIt}
      onClose={handleClose}
      onStepChange={onStepChange}
      style={{
        left: pos.left,
        top: pos.top,
        transformOrigin: isClosing ? closingOrigin : 'center center',
        transform: isClosing ? 'scale(0.05)' : mounted ? 'scale(1)' : 'scale(0.92)',
        opacity: isClosing ? 0 : mounted ? 1 : 0,
        transition: isClosing
          ? 'transform 220ms cubic-bezier(0.4, 0, 1, 1), opacity 180ms ease-in'
          : 'transform 180ms ease-out, opacity 150ms ease-out',
      }}
    >
      {/*
        Arrow — rotated square matching PopoverWrapper dark theme.
        top/bottom: -ARROW_EDGE_INSET places the center 2 px inside the panel edge
        so the outer corner protrudes ~6.5 px as the visible tip.
        marginLeft: -ARROW_HALF centers the square on arrowLeft.
      */}
      <div
        className="absolute bg-app-gray-800 rounded-[2px] pointer-events-none"
        style={{
          width: ARROW_SIZE,
          height: ARROW_SIZE,
          left: arrowLeft,
          marginLeft: -ARROW_HALF,
          transform: 'rotate(45deg)',
          transition: mounted ? 'left 200ms ease' : 'none',
          ...(pos.arrowAtBottom
            ? { bottom: -ARROW_EDGE_INSET, top: 'auto' }
            : { top: -ARROW_EDGE_INSET, bottom: 'auto' }),
        }}
      />
    </GuidePanel>
  );
};

export default BulkTagGuide;
